// EnhancedWebSocketManager.js - Word Add-in Version
// 100% Message Handling Preserved | Chrome Code Removed

export class EnhancedWebSocketManager {
  constructor() {
    this.websocket = null;
    this.isConnected = false;
    this.isExiting = false;
    this.eventListeners = {};
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = Infinity;
    this.reconnectDelay = 1000;
    this.heartbeatInterval = null;
    this.connectionCheckInterval = null;
    this.lastHeartbeat = null;
    this.isRecording = false;
    this.pendingAudioQueue = [];
    this.connectionTimeout = null;
    this.lastConnectionAttempt = 0;
    this.minConnectionInterval = 2000;
    this.isConnecting = false;
    this.reconnectTimeout = null;

    // Notes functionality
    this.notesManager = null;
    this.currentNoteSession = null;
  }

  on(event, callback) {
    if (!this.eventListeners[event]) {
      this.eventListeners[event] = [];
    }
    this.eventListeners[event].push(callback);
  }

  emit(event, data) {
    if (this.eventListeners[event]) {
      this.eventListeners[event].forEach(callback => callback(data));
    }
  }

  setNotesManager(notesManager) {
    this.notesManager = notesManager;
  }

  // === WORD ADD-IN CONNECTION ===
  connect() {
    const now = Date.now();
    
    // Prevent too frequent connection attempts
    if (now - this.lastConnectionAttempt < this.minConnectionInterval) {
      console.log('Connection attempt too soon, delaying...');
      setTimeout(() => this.connect(), this.minConnectionInterval - (now - this.lastConnectionAttempt));
      return;
    }
    
    this.lastConnectionAttempt = now;

    // Prevent connection attempt if already connected or connecting
    if (this.isConnected || this.isConnecting) {
      console.log('WebSocket already connected or connecting, skipping connect attempt');
      return;
    }

    // Check websocket state more thoroughly
    if (this.websocket) {
      const state = this.websocket.readyState;
      if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) {
        console.log('Existing WebSocket still active, skipping connect attempt');
        return;
      }
    }

    console.log('🔌 Attempting to connect to WebSocket (Word Add-in)...');
    this.isConnecting = true;

    try {
      // Clean up any existing connection thoroughly
      this.cleanupConnection();

      // Connect to LOCALHOST (same as Chrome)
      this.websocket = new WebSocket('ws://localhost:8765/word_addin');

      // Clear any existing connection timeout
      if (this.connectionTimeout) {
        clearTimeout(this.connectionTimeout);
        this.connectionTimeout = null;
      }

      // Set connection timeout
      this.connectionTimeout = setTimeout(() => {
        if (this.websocket && this.websocket.readyState === WebSocket.CONNECTING) {
          console.log('Connection timeout, closing and retrying...');
          this.isConnecting = false;
          try {
            this.websocket.close();
          } catch (e) {
            console.error('Error closing timed-out WebSocket:', e);
          }
          this.handleDisconnection();
        }
      }, 8000);

      this.websocket.onopen = () => {
        console.log('✅ WebSocket connected to Doccy desktop app (Word Add-in)');
        this.isConnected = true;
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        
        // Clear connection timeout
        if (this.connectionTimeout) {
          clearTimeout(this.connectionTimeout);
          this.connectionTimeout = null;
        }
        
        this.startHeartbeat();
        this.emit('connected');
        this.emit('showMicIcon', true);
        
        // Process any pending audio
        this.processPendingAudio();
      };

      this.websocket.onmessage = async (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('📨 Received WebSocket message:', data);

          this.lastHeartbeat = Date.now();

          // HANDLE ALL MESSAGES (100% INTACT)
          await this.handleWebSocketMessage(data);

        } catch (error) {
          console.error('Error processing WebSocket message:', error, event.data);
        }
      };

      this.websocket.onclose = (event) => {
        console.log('🔌 WebSocket disconnected:', event.code, event.reason);
        this.isConnecting = false;
        
        // Clear connection timeout
        if (this.connectionTimeout) {
          clearTimeout(this.connectionTimeout);
          this.connectionTimeout = null;
        }

        // Only handle disconnection if it wasn't intentional
        if (!this.isExiting) {
          this.handleDisconnection();
        }
      };

      this.websocket.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
        this.isConnecting = false;
        
        // Clear connection timeout
        if (this.connectionTimeout) {
          clearTimeout(this.connectionTimeout);
          this.connectionTimeout = null;
        }
      };

    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
      this.isConnecting = false;
      this.handleDisconnection();
    }
  }

  // === 100% MESSAGE HANDLING INTACT ===
  async handleWebSocketMessage(data) {
    const messageType = data.type;

    switch (messageType) {
      case 'transcription_result':
        console.log('transcription_result received');
        // Defensive checks + helpful logging for debugging missing fields
        if (data && data.transcription) {
          console.log('Received transcription:', data.transcription);
          const cleanTranscription = (data.transcription || '').trim();
          if (cleanTranscription.length > 0) {
            const payload = {
              raw_transcription: (data.raw_transcription || '').trim(),
              transcription: cleanTranscription
            };
            console.log('Emitting transcription event with payload:', payload);
            this.emit('transcription', payload);
          } else {
            console.log('Empty transcription received, content was empty after trim. Raw message:', data);
          }
        } else {
          console.log('transcription_result received without transcription field:', data);
        }
        break;

      case 'transcription_error':
        console.log('Transcription error handled silently:', data.error);
        break;

      case 'pong':
        console.log('Heartbeat response received');
        break;

      case 'audio_received':
        // Throttle logging for high-frequency audio messages to avoid
        // overwhelming the main thread and DevTools console.
        if (!this._audioReceivedCounter) this._audioReceivedCounter = 0;
        if (!this._lastAudioLogTs) this._lastAudioLogTs = Date.now();

        this._audioReceivedCounter++;
        const now = Date.now();
        if (now - this._lastAudioLogTs > 500) {
          console.log(`Audio chunk activity: received ${this._audioReceivedCounter} chunks in ${now - this._lastAudioLogTs}ms`);
          this._audioReceivedCounter = 0;
          this._lastAudioLogTs = now;
        }
        break;

      case 'arrow_pressed_signal':
        console.log('➡️ Arrow pressed signal received:', data);
        this.emit('arrowPressed', data);
        break;

      case 'save_note':
        console.log('📝 Received save_note message:', data);
        if (this.notesManager) {
          await this.notesManager.handleNoteSaved(data);
        } else {
          console.warn('NotesManager not available for save_note message');
        }
        break;

      case 'note_deleted':
        console.log('🗑️ Received note_deleted message:', data);
        if (this.notesManager) {
          await this.notesManager.handleNoteDeleted(data);
        } else {
          console.warn('NotesManager not available for note_deleted message');
        }
        break;

      case 'mobile_recording_started':
        console.log('🎤 Received mobile_recording_started:', data);
        this.emit('mobileStatus', data.is_mobile_active);
        if (data.is_mobile_active !== undefined) {
          console.log(`📱 Mobile status in mobile_recording_started: ${data.is_mobile_active}`);
        }
        break;

      case 'note_updated':
        console.log('📝 Received note_updated:', data);
        if (this.notesManager) {
          await this.notesManager.handleNoteSaved(data);
        }
        break;

      case 'template_context_acknowledged':
        console.log('📋 Template context acknowledged by server:', data);
        this.emit('templateContextAcknowledged', data);
        break;

      case 'template_context_error':
        console.error('📋 Template context error from server:', data);
        this.emit('templateContextError', data);
        break;

      case 'template_transcription_result':
        console.log('📋 Received template-enhanced transcription:', data);
        if (data.transcription) {
          const cleanTranscription = data.transcription.trim();
          if (cleanTranscription.length > 0) {
            // Emit a consistent object shape so listeners can always expect
            // { transcription, raw_transcription?, templateInfo?, confidence? }
            this.emit('transcription', {
              transcription: cleanTranscription,
              raw_transcription: (data.raw_transcription || '').trim(),
              hasTemplateContext: true,
              templateInfo: data.template_info || null,
              confidence: data.confidence || null
            });
          }
        }
        break;

      case 'magic_correction_response':
        console.log('🔮 Received magic correction response:', data);
        if (data.corrected_text) {
          this.emit('magicCorrectionResult', {
            originalText: data.original_text,
            correctedText: data.corrected_text,
            success: true,
            timestamp: data.timestamp
          });
        } else {
          console.warn('🔮 Magic correction response missing corrected_text');
          this.emit('magicCorrectionResult', {
            success: false,
            error: 'No corrected text received'
          });
        }
        break;

      case 'magic_correction_error':
        console.error('🔮 Magic correction error from server:', data);
        this.emit('magicCorrectionResult', {
          success: false,
          error: data.error || 'Unknown magic correction error',
          originalText: data.original_text
        });
        break;

      case 'magic_correction_acknowledged':
        console.log('🔮 Magic correction request acknowledged by server:', data);
        this.emit('magicCorrectionAcknowledged', data);
        break;

      // case 'radiology_suggestion_response':
      //   console.log('🏥 Received radiology suggestion response:', data);
      //   this.emit('radiologySuggestion', data);
      //   break;

      // case 'radiology_suggestion_error':
      //   console.error('🏥 Radiology suggestion error from server:', data);
      //   this.emit('radiologySuggestionError', data);
      //   break;

      case 'conclusion_generation_response':
        console.log('🏥 Received conclusion generation response:', data);
        this.emit('conclusionGeneration', data);
        break;

      case 'conclusion_generation_error':
        console.error('🏥 Conclusion generation error from server:', data);
        this.emit('conclusionGenerationError', data);
        break;


      default:
        console.log('Unknown message type:', messageType, data);
    }
  }

  // === ALL SEND METHODS INTACT ===
  async sendMagicCorrectionRequest(requestData) {
    if (!this.getConnectionStatus()) {
      console.warn('🔮 WebSocket not connected, cannot send magic correction request');
      return false;
    }
    try {
      const message = {
        type: 'magic_correction_request',
        text: requestData.text,
        mode: requestData.mode || 'chunk_correction',
        timestamp: requestData.timestamp,
        context: requestData.context || null
      };
      this.websocket.send(JSON.stringify(message));
      console.log('🔮 Magic correction request sent to server:', message);
      return true;
    } catch (error) {
      console.error('🔮 Failed to send magic correction request:', error);
      return false;
    }
  }


  async sendConclusionGenerationRequest(requestData) {
    if (!this.getConnectionStatus()) {
      console.warn('🏥 WebSocket not connected, cannot send conclusion generation request');
      return false;
    }
    try {
      const message = {
        type: 'conclusion_generation_request',
        description_content: requestData.description_content,
        username: requestData.username,
        department: requestData.department,
        timestamp: requestData.timestamp
      };
      this.websocket.send(JSON.stringify(message));
      console.log('🏥 Conclusion generation request sent to server:', message);
      return true;
    } catch (error) {
      console.error('🏥 Failed to send conclusion generation request:', error);
      return false;
    }
  }


  async sendNoteCommand(command, data) {
    if (!this.getConnectionStatus()) {
      console.warn('WebSocket not connected, cannot send note command');
      return false;
    }
    try {
      const message = {
        type: 'note_command',
        command: command,
        data: data,
        timestamp: Date.now()
      };
      this.websocket.send(JSON.stringify(message));
      console.log(`📤 Sent note command: ${command}`, data);
      return true;
    } catch (error) {
      console.error('Failed to send note command:', error);
      return false;
    }
  }

  async sendTemplateContext(contextData) {
    if (!this.getConnectionStatus()) {
      console.warn('📋 WebSocket not connected, cannot send template context');
      return false;
    }
    try {
      const message = {
        type: 'template_context',
        context: contextData.context,
        username: contextData.username,
        timestamp: contextData.timestamp,
        department: contextData.department
      };
      this.websocket.send(JSON.stringify(message));
      console.log('📋 Template context sent to server:', message);
      return true;
    } catch (error) {
      console.error('📋 Failed to send template context:', error);
      return false;
    }
  }


  async sendFieldContext(contextData) {
    if (!this.getConnectionStatus()) {
      console.warn('📋 WebSocket not connected, cannot send template context');
      return false;
    }
    try {
      const message = {
        type: 'field_context',
        context: contextData.context,
        username: contextData.username,
        timestamp: contextData.timestamp,
        department: contextData.department
      };
      this.websocket.send(JSON.stringify(message));
      console.log('📋 Template context sent to server:', message);
      return true;
    } catch (error) {
      console.error('📋 Failed to send template context:', error);
      return false;
    }
  }


  async clearTemplateContext(username) {
    if (!this.getConnectionStatus()) {
      console.warn('📋 WebSocket not connected, cannot clear template context');
      return false;
    }
    try {
      const message = {
        type: 'template_context_clear',
        username: username,
        timestamp: Date.now()
      };
      this.websocket.send(JSON.stringify(message));
      console.log('📋 Template context cleared on server');
      return true;
    } catch (error) {
      console.error('📋 Failed to clear template context:', error);
      return false;
    }
  }

  async requestNotesList() {
    return await this.sendNoteCommand('list_notes', {});
  }

  async requestNoteContent(noteId) {
    return await this.sendNoteCommand('get_note', { note_id: noteId });
  }

  async deleteNoteOnServer(noteId) {
    return await this.sendNoteCommand('delete_note', { note_id: noteId });
  }

  sendSettingsUpdate(username, settings) {
    const message = {
      type: 'user_settings_update',
      username: username,
      ragEnabled: settings.ragEnabled,
      conclusionGenerationEnabled: settings.conclusionGenerationEnabled,
      magicCorrectionEnabled: settings.magicCorrectionEnabled,
      timestamp: Date.now()
    };
    return this.send(JSON.stringify(message));
  }

  // === CLEANUP & RECONNECT (INTACT) ===
  cleanupConnection() {
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    this.stopHeartbeat();

    if (this.websocket) {
      try {
        this.websocket.onopen = null;
        this.websocket.onmessage = null;
        this.websocket.onclose = null;
        this.websocket.onerror = null;
        
        const state = this.websocket.readyState;
        if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) {
          this.websocket.close(1000, 'Cleanup');
        }
      } catch (e) {
        console.error('Error cleaning up WebSocket:', e);
      }
      this.websocket = null;
    }
    this.isConnecting = false;
  }

  handleDisconnection() {
    if (this.isExiting) {
      console.log('Disconnection ignored, add-in is exiting');
      return;
    }

    const wasConnected = this.isConnected;
    this.isConnected = false;
    this.isConnecting = false;
    this.stopHeartbeat();
    
    if (wasConnected) {
      this.emit('disconnected');
      this.emit('showMicIcon', false);
    }

    this.scheduleReconnect();
  }

  scheduleReconnect() {
    if (this.isExiting) {
      console.log('Not scheduling reconnect, add-in is exiting');
      return;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('Max reconnect attempts reached, stopping');
      return;
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    this.reconnectAttempts++;
    
    const baseDelay = Math.min(this.reconnectDelay * Math.pow(1.5, Math.min(this.reconnectAttempts - 1, 10)), 30000);
    const jitter = Math.random() * 1000;
    const delay = baseDelay + jitter;
    
    console.log(`🔄 Scheduling reconnect attempt ${this.reconnectAttempts} in ${Math.round(delay)}ms`);

    this.reconnectTimeout = setTimeout(() => {
      if (!this.isExiting && !this.isConnected && !this.isConnecting) {
        console.log(`🔄 Reconnect attempt ${this.reconnectAttempts}`);
        this.connect();
      }
    }, delay);
  }

  startHeartbeat() {
    this.stopHeartbeat();
    this.lastHeartbeat = Date.now();

    this.heartbeatInterval = setInterval(() => {
      if (this.isConnected && this.websocket && this.websocket.readyState === WebSocket.OPEN) {
        try {
          this.websocket.send(JSON.stringify({ 
            type: 'ping', 
            timestamp: Date.now(),
            recording: this.isRecording 
          }));
        } catch (error) {
          console.error('Failed to send heartbeat:', error);
          this.handleDisconnection();
        }
      }
    }, 30000);

    this.connectionCheckInterval = setInterval(() => {
      if (this.isConnected && this.lastHeartbeat) {
        const timeSinceLastHeartbeat = Date.now() - this.lastHeartbeat;
        if (timeSinceLastHeartbeat > 90000) {
          console.log('Heartbeat timeout, reconnecting...');
          this.handleDisconnection();
        }
      }
    }, 20000);
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    if (this.connectionCheckInterval) {
      clearInterval(this.connectionCheckInterval);
      this.connectionCheckInterval = null;
    }
  }

  processPendingAudio() {
    if (this.pendingAudioQueue.length > 0) {
      console.log(`Processing ${this.pendingAudioQueue.length} pending items`);
      const queue = [...this.pendingAudioQueue];
      this.pendingAudioQueue = [];
      queue.forEach(data => this.send(data));
    }
  }

  send(data) {
    if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
      console.log("WebSocket not ready, queueing data");
      this.pendingAudioQueue.push(data);
      if (this.pendingAudioQueue.length > 50) {
        this.pendingAudioQueue = this.pendingAudioQueue.slice(-25);
      }
      if (!this.isConnected && !this.isConnecting) {
        this.connect();
      }
      return false;
    }

    try {
      if (typeof data === "string") {
        this.websocket.send(data);
        console.log("Sent JSON message");
      } else if (data instanceof ArrayBuffer) {
        this.websocket.send(data);
        console.log(`Sent binary audio chunk (${data.byteLength} bytes)`);
      } else {
        console.warn("Unsupported data type for WebSocket send");
      }
      return true;
    } catch (error) {
      console.error("Failed to send data:", error);
      this.pendingAudioQueue.push(data);
      this.handleDisconnection();
      return false;
    }
  }

  setRecordingState(isRecording) {
    this.isRecording = isRecording;
    console.log(`Recording state updated: ${isRecording}`);
  }

  disconnect(exit = false) {
    console.log(`Disconnecting WebSocket (exit: ${exit})`);
    this.isExiting = exit;
    
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    this.stopHeartbeat();

    if (this.websocket) {
      const state = this.websocket.readyState;
      
      if (state !== WebSocket.CLOSED && state !== WebSocket.CLOSING) {
        try {
          if (state === WebSocket.OPEN) {
            this.websocket.send(JSON.stringify({ 
              type: 'disconnect', 
              timestamp: Date.now() 
            }));
          }
          this.websocket.close(1000, 'Add-in disconnect');
          console.log('WebSocket closed intentionally');
        } catch (error) {
          console.error('Error closing WebSocket:', error);
        }
      }
    }
    
    this.cleanupConnection();
    this.isConnected = false;
    this.isConnecting = false;
    
    if (!exit) {
      this.emit('disconnected');
      this.emit('showMicIcon', false);
    }
  }

  getConnectionStatus() {
    return this.isConnected && this.websocket && this.websocket.readyState === WebSocket.OPEN;
  }

  getQueueStatus() {
    return {
      isConnected: this.isConnected,
      isConnecting: this.isConnecting,
      pendingChunks: this.pendingAudioQueue.length,
      isRecording: this.isRecording,
      reconnectAttempts: this.reconnectAttempts,
      currentNoteSession: this.currentNoteSession
    };
  }

  forceReconnect() {
    console.log('Forcing reconnection...');
    this.disconnect(false);
    this.reconnectAttempts = 0;
    this.isExiting = false;
    
    setTimeout(() => {
      this.connect();
    }, 1000);
  }

  clearPendingAudio() {
    const clearedCount = this.pendingAudioQueue.length;
    this.pendingAudioQueue = [];
    console.log(`Cleared ${clearedCount} pending audio chunks`);
    return clearedCount;
  }
}